<template>
    <v-card>
        <v-card-text>
            <v-container
                fluid
            >
                <v-row>
                    <v-col
                        :cols="baseCols"
                    >
                        <v-row>
                            <v-col
                                md="2"
                            >
                                <slot/>
                            </v-col>
                            <v-col
                                md="10"
                            >
                                <v-text-field
                                    v-model="day.title"
                                    :label="$t('day.label.title')"
                                    @input="$v.day.title.$touch()"
                                    @blur="$v.day.title.$touch()"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                md="7"
                            >
                                <v-textarea
                                    v-model="day.description"
                                    :label="$t('label.description')"
                                    @input="$v.day.description.$touch()"
                                    @blur="$v.day.description.$touch()"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                md="2"
                            >
                                <h3>
                                    {{ $t('travel.board') }}
                                </h3>
                                <v-combobox
                                    v-model="day.board"
                                    :items="boards"
                                    :label="$t('day.label.board')"
                                    item-value="value"
                                    item-text="label"
                                    multiple
                                    small-chips
                                    deletable-chips
                                    :return-object="false"
                                    @input="$v.day.board.$touch()"
                                    @blur="$v.day.board.$touch()"
                                >
                                    <template v-slot:selection="{ attrs, item, parent, selected }">
                                        <v-chip
                                            v-bind="attrs"
                                            :input-value="selected"
                                            label
                                            small
                                        >
                                            <span class="pr-2">
                                                {{ $t(`board.${item}`) }}
                                            </span>
                                            <v-icon
                                                small
                                                @click="parent.selectItem(item)"
                                            >
                                                $delete
                                            </v-icon>
                                        </v-chip>
                                    </template>
                                </v-combobox>
                            </v-col>
                            <v-col
                                md="5"
                            >
                                <h3>
                                    {{ $t('day.dayTour') }}
                                </h3>
                                <v-text-field
                                    v-model.number="day.elevationUp"
                                    :label="$t('day.label.elevationUp')"
                                    type="number"
                                    @input="$v.day.elevationUp.$touch()"
                                    @blur="$v.day.elevationUp.$touch()"
                                    :hint="$t('day.hint.numberInMeters')"
                                />
                                <v-text-field
                                    v-model.number="day.elevationDown"
                                    :label="$t('day.label.elevationDown')"
                                    type="number"
                                    @input="$v.day.elevationDown.$touch()"
                                    @blur="$v.day.elevationDown.$touch()"
                                    :hint="$t('day.hint.numberInMeters')"
                                />
                                <v-text-field
                                    v-model="day.duration"
                                    :label="$t('day.label.duration')"
                                    @input="$v.day.duration.$touch()"
                                    @blur="$v.day.duration.$touch()"
                                    :hint="$t('day.hint.formatHHMM')"
                                    :error-messages="durationErrors"
                                />
                                <v-text-field
                                    v-model.number="day.distance"
                                    :label="$t('day.label.distance')"
                                    type="number"
                                    @input="$v.day.distance.$touch()"
                                    @blur="$v.day.distance.$touch()"
                                    :hint="$t('day.hint.numberInKm')"
                                />
                            </v-col>
                            <v-col
                                md="5"
                            >
                                <h3>
                                    {{ $t('day.dayTravel') }}
                                </h3>
                                <v-text-field
                                    v-model="day.driveDuration"
                                    :label="$t('day.label.driveDuration')"
                                    @input="$v.day.driveDuration.$touch()"
                                    @blur="$v.day.driveDuration.$touch()"
                                    :hint="$t('day.hint.formatHHMM')"
                                    :error-messages="driveDurationErrors"
                                />
                                <v-text-field
                                    v-model.number="day.driveDistance"
                                    :label="$t('day.label.driveDistance')"
                                    type="number"
                                    @input="$v.day.driveDistance.$touch()"
                                    @blur="$v.day.driveDistance.$touch()"
                                    :hint="$t('day.hint.numberInKm')"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                md="2"
                            >
                                <h3>
                                    {{ $t('day.label.accommodationCategory') }}
                                </h3>
                                <v-radio-group
                                    v-model="day.hotelCategory"
                                    @input="$v.day.hotelCategory.$touch()"
                                    @blur="$v.day.hotelCategory.$touch()"
                                >
                                    <v-radio
                                        v-for="(category, key) in hotelCategories"
                                        :key="key"
                                        :value="category.value"
                                        :label="category.label"
                                    />
                                </v-radio-group>
                                <v-text-field
                                    v-if="day.hotelCategory === 'custom'"
                                    v-model="day.hotelCategoryCustom"
                                    :label="$t('day.label.hotelCategoryCustom')"
                                    @input="$v.day.hotelCategoryCustom.$touch()"
                                    @blur="$v.day.hotelCategoryCustom.$touch()"
                                />
                            </v-col>
                            <v-col
                                md="5"
                                v-if="day.accommodationData"
                            >
                                <h3>
                                    {{ $t('day.label.accommodation') }}
                                </h3>
                                <v-text-field
                                    v-model="day.accommodationData.name"
                                    :label="$t('day.label.accommodationName')"
                                    @input="$v.day.accommodationData.name.$touch()"
                                    @blur="$v.day.accommodationData.name.$touch()"
                                />
                                <v-text-field
                                    v-model="day.accommodationData.street"
                                    :label="$t('label.street')"
                                    @input="$v.day.accommodationData.street.$touch()"
                                    @blur="$v.day.accommodationData.street.$touch()"
                                />
                                <v-text-field
                                    v-model="day.accommodationData.zip"
                                    :label="$t('label.zip')"
                                    @input="$v.day.accommodationData.zip.$touch()"
                                    @blur="$v.day.accommodationData.zip.$touch()"
                                />
                                <v-text-field
                                    v-model="day.accommodationData.city"
                                    :label="$t('label.city')"
                                    @input="$v.day.accommodationData.city.$touch()"
                                    @blur="$v.day.accommodationData.city.$touch()"
                                />
                                <country-select
                                    :label="$t('label.country')"
                                    v-model="day.accommodationData.country"
                                    @input="$v.day.accommodationData.country.$touch()"
                                    @blur="$v.day.accommodationData.country.$touch()"
                                />
                                <v-checkbox
                                    v-model="day.sameAccommodation"
                                    :label="$t('day.label.sameAccommodation')"
                                    @input="$v.day.sameAccommodation.$touch()"
                                    @blur="$v.day.sameAccommodation.$touch()"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col
                        cols="1"
                    >
                        <slot
                            name="actions"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script>
    import CountrySelect from '../../../Form/CountrySelect';
    import { validationMixin } from 'vuelidate';
    import {mustBeDuration} from "@/validators/duration";

    export default {
        components: {
            CountrySelect,
        },
        mixins: [
            validationMixin,
        ],
        props: {
            value: {
                type: Object,
                default: function () {
                    return {
                        dayFrom: null,
                        dayTo: null,
                        title: null,
                        description: null,
                        board: [],
                        elevationUp: null,
                        elevationDown: null,
                        duration: null,
                        distance: null,
                        driveDuration: null,
                        driveDistance: null,
                        hotelCategory: null,
                        hotelCategoryCustom: null,
                        accommodationData: {
                            name: null,
                            street: null,
                            zip: null,
                            city: null,
                            country: null,
                            sameAccommodation: false,
                        },
                    }
                },
            },
            baseCols: {
                type: Number,
                default: 11,
            },
        },
        data() {
            return {
                day: Object.assign({}, this.value),
            };
        },
        watch: {
            day: {
                deep: true,
                handler: function() {
                    this.$emit('input', {...this.day});
                },
            },
        },
        validations: {
            day: {
                dayFrom: {},
                dayTo: {},
                title: {},
                description: {},
                board: {},
                elevationUp: {},
                elevationDown: {},
                duration: {
                    mustBeDuration,
                },
                distance: {},
                driveDuration: {
                    mustBeDuration,
                },
                driveDistance: {},
                hotelCategory: {},
                hotelCategoryCustom: {},
                accommodationData: {
                    name: {},
                    street: {},
                    zip: {},
                    city: {},
                    country: {},
                    sameAccommodation: {},
                },
            },
        },
        computed: {
            boards() {
                return [
                    {
                        value: 'breakfast',
                        label: this.$t('board.breakfast'),
                    },
                    {
                        value: 'lunch_box',
                        label: this.$t('board.lunch_box'),
                    },
                    {
                        value: 'lunch',
                        label: this.$t('board.lunch'),
                    },
                    {
                        value: 'dinner',
                        label: this.$t('board.dinner'),
                    },
                ];
            },
            hotelCategories() {
                return [
                    {
                        value: 'hotel',
                        label: this.$t('accommodation.category.hotel'),
                    },
                    {
                        value: 'apartment',
                        label: this.$t('accommodation.category.apartment'),
                    },
                    {
                        value: 'bungalow',
                        label: this.$t('accommodation.category.bungalow'),
                    },
                    {
                        value: 'cottage',
                        label: this.$t('accommodation.category.cottage'),
                    },
                    {
                        value: 'guesthouse',
                        label: this.$t('accommodation.category.guesthouse'),
                    },
                    {
                        value: 'homeStay',
                        label: this.$t('accommodation.category.homeStay'),
                    },
                    {
                        value: 'hostel',
                        label: this.$t('accommodation.category.hostel'),
                    },
                    {
                        value: 'houseboat',
                        label: this.$t('accommodation.category.houseboat'),
                    },
                    {
                        value: 'mountainHut',
                        label: this.$t('accommodation.category.mountainHut'),
                    },
                    {
                        value: 'tent',
                        label: this.$t('accommodation.category.tent'),
                    },
                    {
                        value: 'custom',
                        label: this.$t('accommodation.category.custom'),
                    },
                ];
            },
            durationErrors() {
                const errors = [];

                if(!this.$v.day.duration.$dirty) {
                    return errors;
                }
                if(!this.$v.day.duration.mustBeDuration) {
                    errors.push('Duration must be format HH:MM');
                }

                return errors;
            },
            driveDurationErrors() {
                const errors = [];

                if(!this.$v.day.driveDuration.$dirty) {
                    return errors;
                }
                if(!this.$v.day.driveDuration.mustBeDuration) {
                    errors.push('Drive Duration must be format HH:MM');
                }

                return errors;
            },
        }
    }
</script>
